/**
 * Tracks path color state based on boss presence.
 * Boss on grid → fades to red. No boss → fades back to green.
 * Fade duration ~1 second.
 */
export class BossPathColor {
  private progress = 0; // 0 = green, 1 = red
  private target = 0;
  private fadeDuration = 1.0; // seconds

  /** Call when a boss appears on the grid */
  activate(): void {
    this.target = 1;
  }

  /** Call when no bosses remain on the grid */
  deactivate(): void {
    this.target = 0;
  }

  update(dt: number): void {
    if (this.progress === this.target) return;
    const step = dt / this.fadeDuration;
    if (this.target > this.progress) {
      this.progress = Math.min(this.progress + step, 1);
    } else {
      this.progress = Math.max(this.progress - step, 0);
    }
  }

  /** Returns the current path glow color as rgba components (r, g, b) in 0-255 */
  get rgb(): { r: number; g: number; b: number } {
    const p = this.progress;
    // Green: (0, 255, 136) → Red: (255, 45, 85)
    return {
      r: Math.round(0 + p * 255),
      g: Math.round(255 - p * 210),
      b: Math.round(136 - p * 51),
    };
  }

  /** Outer glow rgba string at given alpha */
  glowColor(alpha: number): string {
    const { r, g, b } = this.rgb;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /** Solid hex for packet fill */
  get packetHex(): string {
    const { r, g, b } = this.rgb;
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  }

  get isActive(): boolean {
    return this.target === 1;
  }

  /** Reset to green (e.g. new game) */
  reset(): void {
    this.progress = 0;
    this.target = 0;
  }
}
